import * as Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/boost';
import '../../highcharts-options';

import * as config from './config.json';
import * as utils from '../../../utils';

import { Aeroval } from '../../../types/global';
import { PerformanceData } from '../../../types/data';
declare var aeroval: Aeroval;


export function plotPerformance(data: PerformanceData | undefined, observation: string, parameterDir: string, layer: string, div: string, _chartOptions: any = {}) {
	if (!data) return;

	var modelSeries = []
	const keyTime = utils.getKeyTime(aeroval.time, aeroval.season);
	const models = Object.keys(data)
	let fairmodeFrequency = ''
	let isDataAvailable = false

	for (const model of models) {
		isDataAvailable = data[model] && Object.keys(data[model]).includes(parameterDir)
		if (!isDataAvailable) break

		let modelVar = Object.keys(data[model][parameterDir][observation][layer][model])[0]
		let subData = data[model][parameterDir][observation][layer][model][modelVar][keyTime]

		// model color
		const modelStyle = aeroval.modelStyle[model];
		if (modelStyle) {
			var modelColor = modelStyle.color;
		} else {
			const highchartsColor = Highcharts.getOptions().colors as string[];
			if (highchartsColor) {
				modelColor = highchartsColor[0];
			}
		}

		// compute statistics by summing up values at all stations
		let gap = []
		let fa = []
		let ma = []
		for (let station in subData as any) {
			gap.push(subData[station].gap)
			fa.push(subData[station].fa)
			ma.push(subData[station].ma)
			fairmodeFrequency = subData[station].freq
		}

		// success ratio
		let sr = Number(utils.average(gap)) / (Number(utils.average(gap)) + Number(utils.average(fa)))
		//probability of detection
		let pod = Number(utils.average(gap)) / (Number(utils.average(gap)) + Number(utils.average(ma)))

		modelSeries.push({
			name: model,
			color: modelColor,
			colorAxis: false,
			type: 'scatter',
			data: [{
				x: sr,
				y: pod,
			}]
		})
	}

	if (!isDataAvailable){
		utils.updateAlert('alert', 'warning', 'No performance plot is available for the selected species')
	} else {
		utils.updateAlert('alert')
	}

	// add slopes
	const slopes = [0.1, 0.2, 0.5, 0.8, 1, 1.2, 1.5, 2.0, 4];
	const guidelineSeries = makeGuideLines(slopes)
	const csi = makeCSI();
	const performanceThreshold = (config as any)[aeroval.parameter.dir]

	let titleText = `${aeroval.parameter.name} ${fairmodeFrequency} mean performance diagram for ${performanceThreshold} µg/m3`
	let xAxisTitle = 'Succes Ratio'
	let yAxisTitle = 'Probability of Detection'


	Highcharts.chart({
		chart: {
			renderTo: div,
			marginLeft: 150,
			marginRight: 150,
			spacingLeft: 0,
			height: 650,
		},
		title: {
			text: titleText,
		},
		subtitle: {
			text: `${observation} - ${fairmodeFrequency} data`,
		},
		pane: {
			startAngle: 0,
			endAngle: 90,
		},
		xAxis: [
			{
				tickInterval: 0.2,
				min: 0,
				max: 1,
				lineWidth: 1,
				minorGridLineWidth: 1,
				visible: true,
				title: {
					text: xAxisTitle,
				},
				gridLineWidth: 0,
				opposite: false,
				labels: {
					formatter: function () {
						const label = parseFloat(this.axis.defaultLabelFormatter.call(this));
						return String(Math.abs(label));
					},
				},
			},
			{
				linkedTo: 0,
				opposite: true,
			},
		],
		yAxis: [
			{
				min: 0,
				max: 1,
				tickInterval: 0.2,
				minorGridLineWidth: 1,
				title: {
					text: yAxisTitle,
				},
				gridLineWidth: 0,
				lineWidth: 0,
			},
			{
				linkedTo: 0,
				opposite: true,
				gridLineWidth: 0,
				lineWidth: 0,
				title: {
					text: '',
				},
			},
		],
		tooltip: {
			headerFormat: '',
			pointFormat: '<b>{series.name}</b><br>x: {point.x:%.2f}<br>y: {point.y:%.2f}',
		},
		legend: {
			enabled: true,
			layout: 'horizontal',
			align: 'center',
			verticalAlign: 'bottom',
			floating: false,
			y: 10,
			x: 0,
			itemMarginBottom: 5,
			symbolWidth: 12,
			symbolHeight: 12
		},
		colorAxis: {
			min: 0,
			max: 1,
			stops: [
				[0.0, 'rgba(215, 48, 39, 0.50)'],
				[0.3, 'rgba(252, 141, 89, 0.50)'],
				[0.5, 'rgba(254, 224, 139, 0.50)'],
				[0.7, 'rgba(217, 239, 139, 0.50)'],
				[1.0, 'rgba(26, 152, 80, 0.50)']
			],
			showInLegend: false
		},
		series: [
			csi,
			...guidelineSeries,
			...modelSeries
		] as any,
		plotOptions: {
			series: {
				events: {
					legendItemClick: function () {
						utils.clickOnLegendItem(this);
					},
				},
				states: {
					inactive: {
						enabled: false,
					},
				},
			},
		},
	})
}

function makeGuideLines(slopes: number[]): Highcharts.SeriesOptionsType[] {
	return slopes.map((slope) => {
		return {
			type: 'line',
			name: String(slope),
			animation: false,
			dashStyle: 'Dash',
			color: 'gray',
			lineWidth: 1,
			marker: { enabled: false },
			enableMouseTracking: false,
			showInLegend: false,

			// Three points: start, end, label position
			data: [
				{ x: 0, y: 0 },
				{ x: 1, y: slope }
			],
			dataLabels: {
				enabled: true,
				formatter: function (this: Highcharts.Point): string {
					return ((this as any).custom?.label as string | undefined) ?? '';
				},
				align: 'center',
				verticalAlign: 'middle',
				padding: 0,
				y: 0
			},
			tooltip: {
				enabled: false,
			},
		} as Highcharts.SeriesOptionsType
	});
}

function makeCSI() {
	const size = 100;
	const data: number[][] = [];

	for (let i = 0; i < size; i++) {
		const x = (i + 0.5) / size;           // center of bin → nicer visual
		const row: number[] = [];
		for (let j = 0; j < size; j++) {
			const y = (j + 0.5) / size;
			const denom = (1 / x) + (1 / y) - 1;
			const csi = denom > 1e-6 ? 1 / denom : 0;
			row.push(csi);
		}
		data.push(row);
	}
	return {
		type: 'heatmap',
		name: 'CSI',
		showInLegend: false,
		data: data.flatMap((row, y) =>
			row.map((value, x) => [x / 99, y / 99, value])
		),
		colsize: 1 / 99,
		rowsize: 1 / 99,
		opacity: 0.2,
		tooltip: {
			headerFormat: '',
			pointFormat: '<b>x:</b> {point.x:.2f}<br><b>y:</b> {point.y:.2f}<br><b>CSI:</b> {point.value:.3f}'
		}
	};
}